import { CheckCircleIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { SettingKey } from 'config'
import { useProductTypeOptions } from 'hooks/useProductTypeOptions'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { getAdminConfig, setAdminConfig } from 'services'

import { ILoanGlobalSettings, PricingRuleOptions } from '../../GlobalSettings'

export const PricingRuleOptionsMapping: Record<PricingRuleOptions, string> = {
  [PricingRuleOptions.RTL_TRADER]: 'RTL Trader',
  [PricingRuleOptions.MANUAL_PRICING]: 'Manual Pricing',
}

const pricingRuleDescriptions: Record<PricingRuleOptions, string> = {
  [PricingRuleOptions.RTL_TRADER]: 'Rates and prices are pulled automatically from RTL Trader.',
  [PricingRuleOptions.MANUAL_PRICING]: 'Rates and prices are entered by hand.',
}

export const PricingRule = () => {
  const { options: productTypeOptions, label: productTypeLabel } = useProductTypeOptions()
  const [globalSetting, setGlobalSetting] = useState<ILoanGlobalSettings>()
  const [pricingRules, setPricingRules] = useState<Partial<Record<string, PricingRuleOptions>>>({})
  const [isChanged, setChanged] = useState(false)
  const [isLoading, setLoading] = useState(true)
  const [showMissing, setShowMissing] = useState(false)

  useEffect(() => {
    setLoading(true)
    getAdminConfig(SettingKey.GLOBAL_SETTINGS)
      .then((res) => {
        setGlobalSetting(res)
        setPricingRules(res?.pricingRules || {})
      })
      .finally(() => setLoading(false))
  }, [])

  const selectedCount = useMemo(
    () => productTypeOptions.filter((productType) => !!pricingRules[productType]).length,
    [pricingRules, productTypeOptions],
  )

  const onChange = (productType: string, value: PricingRuleOptions) => {
    setPricingRules((prev) => ({ ...prev, [productType]: value }))
    setChanged(true)
  }

  const onSubmit = async () => {
    if (!globalSetting) return

    const missing = productTypeOptions.filter((productType) => !pricingRules[productType])
    if (missing.length) {
      setShowMissing(true)
      toast('Select a pricing rule for every product type.', { type: 'error' })
      return
    }

    const newSetting = cloneDeep(globalSetting)
    newSetting.pricingRules = pricingRules as ILoanGlobalSettings['pricingRules']

    setLoading(true)
    try {
      await setAdminConfig(SettingKey.GLOBAL_SETTINGS, newSetting)
      setGlobalSetting(newSetting)
      setChanged(false)
      setShowMissing(false)
      toast('Successfully saved!', { type: 'info' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative mt-2">
      <LayoutLoading show={isLoading} />
      <SaveChanges show={isChanged} label="Save Changes" onSave={onSubmit} />

      <div className="mb-4 border-b px-1 pb-3">
        <h2 className="text-base font-bold text-gray-900">Pricing Rules</h2>
        <p className="mt-1 text-sm text-gray-500">
          Choose how each product type is priced. Only one rule can be active for a product type.
        </p>
        <p className="mt-2 text-xs font-medium text-shade-blue">
          {selectedCount} of {productTypeOptions.length} product types configured
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        {productTypeOptions.map((productType) => {
          const selected = pricingRules[productType]
          const isMissing = showMissing && !selected

          return (
            <div
              key={productType}
              className={`rounded-lg border bg-white p-4 shadow-sm ${
                isMissing ? 'border-rose-300' : 'border-gray-200'
              }`}
            >
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{productTypeLabel(productType)}</h3>
                  <p className="mt-0.5 text-sm text-gray-500">
                    {selected ? pricingRuleDescriptions[selected] : 'No pricing rule selected yet.'}
                  </p>
                </div>
                {selected && <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />}
              </div>

              <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={productTypeLabel(productType)}>
                {Object.values(PricingRuleOptions).map((option) => {
                  const isSelected = selected === option
                  return (
                    <button
                      key={option}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() => onChange(productType, option)}
                      className={`rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                        isSelected
                          ? 'border-shade-blue bg-shade-blue/10 font-semibold text-shade-blue'
                          : 'border-gray-200 text-gray-700 hover:border-shade-blue/40 hover:bg-gray-50'
                      }`}
                    >
                      {PricingRuleOptionsMapping[option]}
                    </button>
                  )
                })}
              </div>
              {isMissing && <p className="mt-2 text-xs text-rose-600">Select a pricing rule for this product type.</p>}
            </div>
          )
        })}
      </div>
    </div>
  )
}
